import React from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Heading,
  Text,
  Button,
  Icon,
  Flex,
  Link,
  useColorModeValue,
  Stack,
  Divider,
} from '@chakra-ui/react';
import { FaPhone, FaExternalLinkAlt, FaMapMarkerAlt, FaDirections } from 'react-icons/fa';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';

const ShopLocations = () => {

  const shops = [
    {
      id: 1,
      branch: "YK",
      name: "iFun baby Store (YK Shop)",
      address: "အမှတ်(၁၆)၊ အရိယမဂ္ဂင်လမ်းမ၊ မြေညီထပ်၊ ရန်ကင်းမြို့နယ်၊ ရန်ကုန်မြို့",
      phone: "09780985157, 09886644004",
      position: { lat: 16.8309, lng: 96.1654 },
      mapUrl: "https://www.google.com/maps/place/iFun+Baby+-+YK+(By+USAvitamin+Store+for+Myanmar)/@16.8309692,96.1654497,17z/data=!4m6!3m5!1s0x30c193ad103461f9:0xaeeca1a50747af1!8m2!3d16.8309558!4d96.1654484!16s%2Fg%2F11hnlsc192?entry=ttu",
    },
    {
      id: 2,
      branch: "KMD",
      name: "iFun baby Store (KMD Shop)",
      address: "အမှတ်(၆၂)၊ မြေညီထပ်၊ သီတာလမ်း၊ ကြည့်မြင်တိုင်မြို့နယ်၊ ရန်ကုန်မြို့",
      phone: "0977664003, 09886644003",
      position: { lat: 16.7953, lng: 96.1256 },
      mapUrl: "https://www.google.com/maps/place/Ifunbaby/@16.7949377,96.1254567,20.52z/data=!4m6!3m5!1s0x30c1eb002dc32ab5:0xf272806068f1813b!8m2!3d16.7949402!4d96.1255846!16s%2Fg%2F11y2bn6g3l?entry=ttu",
    },
    {
      id: 3,
      branch: "LMD",
      name: "iFun baby Store (LMD Shop)",
      address: "အမှတ်(၁၀၈)၊ မြေညီထပ်၊ ၁၅လမ်း(အလယ်ဘလောက်)၊ လမ်းမတော်မြို့နယ်၊ ရန်ကုန်မြို့",
      phone: "0977664002, 09886644002",
      position: { lat: 16.7761, lng: 96.1468 },
      mapUrl: "https://www.google.com/maps/place/iFun+Baby+-+LMD+(By+Usavitaminstore+for+Myanmar)/@16.7760691,96.1442227,17z/data=!3m1!4b1!4m6!3m5!1s0x30c1eb78dd5e2329:0xdb56b11e10ca8c27!8m2!3d16.7760691!4d96.1467976!16s%2Fg%2F11fmlb9m0s?entry=ttu",
    },
  ];

  // Theme colors
  const primaryColor = useColorModeValue('blue.800', 'custom.900');
  const boxBgColor = useColorModeValue('gray.50', 'gray.700');
  const boxTextColor = useColorModeValue('custom.800', 'gray.200');
  const bgColor = useColorModeValue('teal.600', 'gray.800');
  const textColor = useColorModeValue('custom.900', 'gray.200');
  const {t} = useTranslation();
  const navigate = useNavigate(); 

  return (
    <Flex
      minH="100vh"
      direction="column"
      align="center"
      bg={bgColor}
      color={textColor}
      width="100%"
      pt={`calc(1rem + env(safe-area-inset-top))`}
      // paddingTop="20px"
    >
      {/* Header */}
      <Box display="flex" alignItems="center" width="100%" p={2} mb={4}>
        <FontAwesomeIcon
          onClick={() => navigate('/')}
          icon={faArrowLeft}
          size="xl"
          style={{ cursor: 'pointer', marginRight: '8px' }}
        />
        <Text fontSize="xl" fontWeight="bold">
          {t('menu.items.shop_locations')}
        </Text>
      </Box>

      {/* Main Content Box */}
      <Box
        minH={'100vh'}
        w="full"
        maxW="lg"
        bg={boxBgColor}
        color={boxTextColor}
        borderRadius="2xl"
        p={4}
        boxShadow="md"
      >
        <Stack spacing={4}>
          {shops.map((shop, index) => (
            <Box key={index} p={4} bg="whiteAlpha.100" borderRadius="xl" boxShadow="sm" borderWidth="1px">
              <Heading size="sm" color={primaryColor} mb={2}>
                {shop.name}
              </Heading>
              <Divider mb={3}/>
              
              <Flex align="flex-start" mb={2}>
                <Icon as={FaMapMarkerAlt} color="red.600" mt={1} mr={2} />
                <Text fontSize="xs">{shop.address}</Text>
              </Flex>

              <Flex align="center" mb={3} wrap="wrap">
                <Icon as={FaPhone} color="teal.400" mr={2} />
                {shop.phone.split(',').map((number, i) => (
                  <Link
                    key={i}
                    href={`tel:${number.trim()}`}
                    color={'gray.500'}
                    fontWeight="bold"
                    mr={2}
                    fontSize="xs"
                    _hover={{ textDecoration: 'underline' }}
                  >
                    {number.trim()}
                  </Link>
                ))}
              </Flex>

              <Flex align="flex-start" gap={4} mb={2}>
              <Button
                fontSize='xs'
                as="a"
                href={shop.mapUrl}
                target="_blank"
                rel="noopener noreferrer"
                colorScheme="teal"
                size="sm"
                rightIcon={<Icon as={FaExternalLinkAlt} />}
                
              >
                {t('locations.view_on_map')}
              </Button>
              <Button
                  fontSize='xs'
                  size="sm"
                  as="a"
                  href={`https://www.google.com/maps/dir/?api=1&destination=${shop.position.lat},${shop.position.lng}`}
                  target="_blank"
                  colorScheme="teal"
                  rightIcon={<Icon as={FaDirections} />}
                >
                  {t('locations.to_shop')}
              </Button>
              </Flex>
            </Box>
          ))}

          {/* Directions Section */}
          {/* <Box>
            <Heading size="sm" mb={3} color={primaryColor}>
              {t('locations.get_directions')}
            </Heading>

            <Stack spacing={3}>
              <Button
                fontSize='xs'
                size="sm"
                as="a"
                href={`https://www.google.com/maps/dir/?api=1&destination=${shops[0].position.lat},${shops[0].position.lng}&waypoints=${shops[1].position.lat},${shops[1].position.lng}|${shops[2].position.lat},${shops[2].position.lng}`}
                target="_blank"
                colorScheme="teal"
                rightIcon={<Icon as={FaDirections} />}
              >
                {t('locations.view_all_shops_route')}
              </Button>
              <br/>
            </Stack>
          </Box> */}
        </Stack>
      </Box>
    </Flex>

  );
};

export default ShopLocations;